import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { computed, DestroyRef, inject, Injectable, PLATFORM_ID, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, merge, startWith } from 'rxjs';

// Accepting any of these Usercentrics services releases GTM dataLayer events
const TRACKING_CONSENT_SERVICE_NAMES: string[] = ['Google Analytics', 'PETS - Personal Event Tracking System'];

const CONSENT_UPDATE_EVENTS: readonly string[] = ['UC_UI_INITIALIZED', 'UC_UI_CMP_EVENT', 'UC_UI_VIEW_CHANGED'];

interface UsercentricsServiceInfo {
  name: string;
  consent: {
    status: boolean;
  };
}

declare global {
  interface Window {
    UC_UI: {
      showSecondLayer(): void;
      showFirstLayer(): void;
      isConsentRequired(): boolean;
      getServicesBaseInfo(): UsercentricsServiceInfo[];
    } | undefined;
    UC_UI_SUPPRESS_CMP_DISPLAY?: boolean;
  }
}

@Injectable({
  providedIn: 'root',
})
export class TrackingConsentService {
  public readonly given: Signal<boolean> = computed(() => this.consentGiven());

  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly document: Document = inject(DOCUMENT);
  private readonly isBrowser: boolean = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly consentGiven: WritableSignal<boolean> = signal(false);
  private observing: boolean = false;

  public update(given: boolean): void {
    this.consentGiven.set(given);
  }

  public observeUsercentrics(): void {
    const view: Window | null = this.isBrowser ? this.document.defaultView : null;

    if (!view || this.observing) {
      return;
    }

    this.observing = true;

    merge(...CONSENT_UPDATE_EVENTS.map(eventName => fromEvent(view, eventName)))
      .pipe(
        // reads the current state too, as the CMP may already be initialized (e.g. by a host page)
        startWith(undefined),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.update(this.hasUsercentricsTrackingConsent(view));
      });
  }

  private hasUsercentricsTrackingConsent(view: Window): boolean {
    const services: UsercentricsServiceInfo[] = view.UC_UI?.getServicesBaseInfo() ?? [];

    return services.some(service => service.consent.status && TRACKING_CONSENT_SERVICE_NAMES.includes(service.name));
  }
}
